import React, { useState, useRef } from 'react';
import axios from 'axios';
import { API_URL, getAuthHeader } from '../../utils/config';

export default function GroupModal({ show, onClose, users, onCreate }) {
  const [groupName, setGroupName] = useState('');
  const [description, setDescription] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  if (!show) return null;

  const filtered = search.trim()
    ? users.filter(u => u.full_name.toLowerCase().includes(search.toLowerCase()) || (u.department||'').toLowerCase().includes(search.toLowerCase()))
    : users;

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
  const allFilteredSelected = filtered.length > 0 && filtered.every(u => selected.includes(u.id));
  const selectAllFiltered = () => setSelected(prev => [...new Set([...prev, ...filtered.map(u => u.id)])]);
  const clearAll = () => setSelected([]);

  const handleCreate = async () => {
    if (!groupName.trim()) return alert('Enter group name');
    if (selected.length < 2) return alert('Select at least 2 members');
    setCreating(true);
    try {
      await onCreate(groupName, description, selected, avatarFile);
      // Reset
      setGroupName(''); setDescription(''); setSelected([]); setAvatarFile(null); setAvatarPreview(null); setSearch('');
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e=>e.stopPropagation()}>
        
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between" style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-white text-lg">👥</div>
            <div>
              <h3 className="font-bold text-white text-base">Create Group</h3>
              <p className="text-white/70 text-xs">Add members and customize</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition">✕</button>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto" style={{maxHeight:'75vh'}}>
          
          {/* Avatar + Name row */}
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-2xl font-bold overflow-hidden cursor-pointer shadow-md"
                onClick={() => fileRef.current?.click()}>
                {avatarPreview ? <img src={avatarPreview} className="w-full h-full object-cover" alt=""/> : (groupName?.[0]?.toUpperCase() || '👥')}
              </div>
              <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center cursor-pointer shadow text-xs text-white">
                📷<input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { const f=e.target.files[0]; if(f){setAvatarFile(f);setAvatarPreview(URL.createObjectURL(f));} }}/>
              </label>
            </div>
            <div className="flex-1">
              <input type="text" placeholder="Group name *" value={groupName} onChange={e=>setGroupName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 transition font-semibold"/>
              <p className="text-xs text-gray-400 mt-1">Click avatar to add group photo</p>
            </div>
          </div>

          {/* Description */}
          <textarea placeholder="Group description (optional)" value={description} onChange={e=>setDescription(e.target.value)} rows={2}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none transition"/>

          {/* Member search */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Add Members</label>
              <div className="flex items-center gap-2">
                {selected.length > 0 && <span className="text-xs text-teal-600 dark:text-teal-400 font-bold">{selected.length} selected</span>}
                {!allFilteredSelected && filtered.length > 0 && (
                  <button onClick={selectAllFiltered}
                    className="text-xs px-2 py-1 rounded-lg font-semibold text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition"
                    style={{border:'1px solid rgba(13,148,136,0.3)'}}>
                    {search.trim() ? `Select all ${filtered.length} shown` : `Select all (${filtered.length})`}
                  </button>
                )}
                {selected.length > 0 && (
                  <button onClick={clearAll}
                    className="text-xs px-2 py-1 rounded-lg font-semibold text-gray-400 hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition"
                    style={{border:'1px solid rgba(156,163,175,0.3)'}}>
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="relative mb-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input type="text" placeholder="Search by name or department..." value={search} onChange={e=>setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-400 transition"/>
            </div>

            {/* Selected chips */}
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selected.slice(0, 15).map(id => {
                  const u = users.find(x=>x.id===id);
                  return u ? (
                    <span key={id} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-teal-700 dark:text-teal-300"
                      style={{background:'rgba(13,148,136,0.12)',border:'1px solid rgba(13,148,136,0.2)'}}>
                      {u.full_name.split(' ')[0]}
                      <button onClick={()=>toggle(id)} className="text-teal-500 hover:text-red-400 ml-0.5">×</button>
                    </span>
                  ) : null;
                })}
                {selected.length > 15 && (
                  <span className="px-2.5 py-1 rounded-lg text-xs font-medium text-gray-500 dark:text-gray-400" style={{background:'rgba(156,163,175,0.12)'}}>
                    +{selected.length - 15} more
                  </span>
                )}
              </div>
            )}

            {/* User list */}
            <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700">
              {filtered.map(u => {
                const isSel = selected.includes(u.id);
                return (
                  <div key={u.id} onClick={()=>toggle(u.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition ${isSel?'bg-teal-50 dark:bg-teal-900/20':'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                      {u.avatar_url?.startsWith('/uploads/')
                        ? <img src={`https://qtalk.qualtechedge.in${u.avatar_url}`} className="w-full h-full object-cover" alt=""/>
                        : u.full_name[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{u.full_name}</p>
                      <p className="text-xs text-gray-400 truncate">{u.designation||u.department||u.email}</p>
                    </div>
                    {isSel && <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Create button */}
          <button onClick={handleCreate} disabled={creating || !groupName.trim() || selected.length < 2}
            className="w-full py-3 rounded-xl font-bold text-white text-sm transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
            {creating ? (
              <><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>Creating...</>
            ) : `👥 Create Group (${selected.length} members)`}
          </button>
        </div>
      </div>
    </div>
  );
}
